import { ElementType } from "react";

import { Heading, HeadingOptions } from "@tiptap/extension-heading";
import { Node } from "@tiptap/pm/model";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

import setNodeRef from "@/view/ProseMirrorNode/utils/setNodeRef";
import ReadAloudSpans from "@/view/ReadAloudControls/ReadAloudSpans";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode/useReadAloudMode";
import NodeWithTranslation from "@/view/TiptapArticleBody/components/NodeWithTranslation";
import { ComponentPropsWithIdentifier } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";

export interface HeadingBlockOptions extends HeadingOptions {
  disableTranslation?: boolean;
}

export const HeadingBlockComponent = ({
  editor,
  node,
  extension,
}: ComponentPropsWithIdentifier<HeadingBlockOptions>) => {
  const { isReadAloudModeActive, readAloudSpanRefs } = useReadAloudMode();
  const headingText = node.textContent;
  const identifier = `${node.attrs["data-identifier"] || ""}`;
  const level = node.attrs["level"];
  const { disableTranslation = false } = extension?.options;

  let headingElement;
  if (level) {
    headingElement = `h${level}` as ElementType;
  } else {
    const match = identifier.match(/^\/([a-zA-Z0-9-_]+)/);
    headingElement = (match?.[1] as ElementType) || "h2";
  }

  node.identifier = identifier;

  return isReadAloudModeActive ? (
    <NodeViewWrapper
      as={headingElement}
      ref={(ref: Node) => setNodeRef(ref, readAloudSpanRefs, node)}
    >
      <ReadAloudSpans startOffsetRelativeToParent={0} text={headingText} />
    </NodeViewWrapper>
  ) : (
    <NodeWithTranslation
      state={editor.state}
      node={node}
      props={{ "data-identifier": identifier, as: headingElement }}
      disableTranslation={disableTranslation}
    />
  );
};

export const HeadingBlock = Heading.extend<HeadingBlockOptions>({
  name: "headings_block",
  addAttributes() {
    return {
      "data-identifier": {
        default: null,
      },
      level: {
        default: "",
      },
    };
  },
  addOptions() {
    return {
      ...this.parent?.(),
      disableTranslation: false,
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(HeadingBlockComponent);
  },
});
